/**
 * frontend/components/Turnstile.jsx — Cloudflare Turnstile bot check for sign-in.
 *
 * Why: from Oct 15 2026 a script in Frankfurt requested ~50–120 magic links a day to strangers'
 * addresses (fake old browser user agents, nobody ever signed in), using up the shared Resend
 * quota that real users' login emails need. Supabase Auth's CAPTCHA protection rejects
 * /otp (and anonymous sign-in) requests that don't carry a valid Turnstile token.
 *
 * Off unless REACT_APP_TURNSTILE_SITE_KEY is set at build time, so this can ship before
 * CAPTCHA is switched on in Supabase. Order: deploy with the site key → then enable CAPTCHA in
 * Supabase (Auth → Attack Protection) with the secret key. The other order breaks sign-in.
 *
 * Usage: <Turnstile ref={ref} onToken={setToken} onExpire={() => setToken(null)} />
 *   ref.current.reset() — tokens are single-use: reset after every request that spent one.
 * "interaction-only": invisible unless Cloudflare wants the visitor to tick the box.
 */

import React, { forwardRef, useEffect, useImperativeHandle, useRef } from 'react';

export const TURNSTILE_SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY || '';
const SCRIPT_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';

let scriptPromise = null;
function loadTurnstile() {
  if (typeof window === 'undefined') return Promise.reject(new Error('no window'));
  if (window.turnstile) return Promise.resolve(window.turnstile);
  if (!scriptPromise) {
    scriptPromise = new Promise((resolve, reject) => {
      const s = document.createElement('script');
      s.src = SCRIPT_SRC;
      s.async = true;
      s.defer = true;
      s.onload = () => (window.turnstile ? resolve(window.turnstile) : reject(new Error('turnstile missing')));
      s.onerror = () => { scriptPromise = null; reject(new Error('turnstile script failed to load')); };
      document.head.appendChild(s);
    });
  }
  return scriptPromise;
}

const Turnstile = forwardRef(function Turnstile({ siteKey = TURNSTILE_SITE_KEY, onToken, onExpire, onError, action = 'login' }, ref) {
  const box = useRef(null);
  const widget = useRef(null);
  // Latest callbacks without re-rendering the widget.
  const cb = useRef({ onToken, onExpire, onError });
  cb.current = { onToken, onExpire, onError };

  useImperativeHandle(ref, () => ({
    reset() {
      try { if (widget.current != null) window.turnstile?.reset(widget.current); } catch { /* widget gone */ }
    },
  }), []);

  useEffect(() => {
    if (!siteKey) return undefined;
    let cancelled = false;
    loadTurnstile().then((ts) => {
      if (cancelled || !box.current) return;
      widget.current = ts.render(box.current, {
        sitekey: siteKey,
        action,
        appearance: 'interaction-only',
        size: 'flexible',
        theme: 'dark',
        callback: (token) => cb.current.onToken?.(token),
        'expired-callback': () => cb.current.onExpire?.(),
        'error-callback': () => { cb.current.onError?.(); },
      });
    }).catch(() => { if (!cancelled) cb.current.onError?.(); });
    return () => {
      cancelled = true;
      try { if (widget.current != null) window.turnstile?.remove(widget.current); } catch { /* already gone */ }
      widget.current = null;
    };
  }, [siteKey, action]);

  if (!siteKey) return null;
  return <div ref={box} className="auth-turnstile" />;
});

export default Turnstile;
